import { rpcSlice } from '../../../store/api/rpcSlice';

export interface ChatRestoreBoundary {
  run_id: string;
  seq: number;
}

export interface ChatRestoreWindow {
  max_events: number;
  max_runs: number;
  returned_events: number;
  returned_runs: number;
  has_more: boolean;
  next_cursor?: string;
}

export interface ChatRestoreEvent {
  run_id: string;
  seq: number;
  event_type: 'user' | 'sem' | 'done' | 'failed' | 'cancelled' | string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface ChatConversationRestoreResponse {
  conversation_id: string;
  profile_slug: string;
  restore_status: 'ok' | 'empty' | 'partial';
  source: 'run_events' | string;
  truncated: boolean;
  dropped_events: number;
  window: ChatRestoreWindow;
  page_bounds?: {
    oldest?: ChatRestoreBoundary;
    newest?: ChatRestoreBoundary;
  };
  events: ChatRestoreEvent[];
}

export interface ChatConversationRestoreParams {
  conversationId: string;
  cursor?: string;
  maxEvents?: number;
  maxRuns?: number;
}

export interface ArtifactConversationResolveResponse {
  artifact_id: string;
  revision?: number;
  conversation_id?: string;
  run_id?: string;
  status: 'found' | 'not_found';
}

export interface ArtifactConversationResolveParams {
  artifactId: string;
  revision?: number;
}

export const chatRestoreApi = rpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    getChatConversationRestore: builder.query<
      ChatConversationRestoreResponse,
      ChatConversationRestoreParams
    >({
      query: ({ conversationId, cursor, maxEvents, maxRuns }) => ({
        url: `/chat/conversations/${conversationId}/restore`,
        method: 'GET',
        params: {
          ...(cursor ? { cursor } : {}),
          ...(typeof maxEvents === 'number' ? { max_events: String(maxEvents) } : {}),
          ...(typeof maxRuns === 'number' ? { max_runs: String(maxRuns) } : {}),
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'Chat', id: `restore:${arg.conversationId}` },
      ],
    }),
    resolveArtifactConversation: builder.query<
      ArtifactConversationResolveResponse,
      ArtifactConversationResolveParams
    >({
      query: ({ artifactId, revision }) => ({
        url: `/chat/artifacts/${artifactId}/conversation`,
        method: 'GET',
        params: {
          ...(typeof revision === 'number' ? { revision: String(revision) } : {}),
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'Chat', id: `artifact-conv:${arg.artifactId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useLazyGetChatConversationRestoreQuery, useLazyResolveArtifactConversationQuery } =
  chatRestoreApi;
